(ns io.daskul.webapp.widgets.dashboard.page
  "Dashboard page — fetches data and renders all sections.

   Sections are split into three groups (see widgets.dashboard.layout):
   - :live      current state, no date filter (e.g. Action Items)
   - :snapshot  current-state business snapshot, no date filter (e.g. Overview)
   - :analytics responds to the date-range selector

   They render top-to-bottom :live → :snapshot → :analytics; the date-range
   selector sits in the page header (shown when an :analytics block is present),
   governing the whole page.

   Generic component that works with any product's dashboard endpoint.
   Just require this namespace and render `($ dashboard-page)`."
  (:require
   [uix.core :refer [defui $ use-effect use-callback use-memo]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.dashboard.events :as dashboard.events]
   [io.daskul.webapp.widgets.dashboard.subs :as dashboard.subs]
   [io.daskul.webapp.widgets.dashboard.section :as section]
   [io.daskul.webapp.widgets.dashboard.layout :as layout]
   [io.daskul.webapp.widgets.dashboard.dates :as dates]
   [io.daskul.webapp.widgets.dashboard.date-range :as date-range]))

(defui dashboard-page
  "Full dashboard page — fetches sections from API and renders them.
   Sections are grouped by :group and render top-to-bottom
   :live → :snapshot → :analytics; only the analytics block gets a
   date-range selector.

   Props (both optional):
     :title  page heading — defaults to \"Dashboard\"
     :groups which groups to render, e.g. [:snapshot :analytics] — defaults to
             all. One endpoint can then feed more than one page."
  [{:keys [title subtitle groups] :or {title "Dashboard"}}]
  (let [sections   (urf/use-subscribe [::dashboard.subs/sections])
        loading?   (urf/use-subscribe [::dashboard.subs/loading?])
        error      (urf/use-subscribe [::dashboard.subs/error])
        date-range (urf/use-subscribe [::dashboard.subs/date-range])
        on-refresh (use-callback
                    (fn []
                      (if date-range
                        (rf/dispatch [::dashboard.events/fetch
                                      {:start-date (:start-date date-range)
                                       :end-date   (:end-date date-range)}])
                        (rf/dispatch [::dashboard.events/fetch])))
                    [date-range])
        blocks     (use-memo
                    (fn []
                      (layout/section-blocks sections groups))
                    [sections groups])]

    (use-effect
     (fn []
       (rf/dispatch [::dashboard.events/fetch])
       js/undefined)
     [])

    (cond
      ;; #749: only the FIRST load blanks the page. A re-fetch keeps the tree
      ;; mounted — swapping it for a loader unmounted the date-range selector
      ;; mid-edit and threw away the custom range the user had just applied.
      (layout/first-load? loading? sections)
      ($ ui/center {:mih 200}
        ($ ui/loader))

      error
      ($ ui/stack {:gap "md"}
        ($ ui/group {:justify "space-between" :align "center"}
          ($ ui/title {:order 2} title)
          ($ ui/action-icon {:variant "default" :size "lg" :onClick on-refresh}
            ($ icons/icon-refresh {:size 18 :stroke 1.5})))
        ($ ui/alert {:variant "light" :color "red" :title "Error"}
          error))

      :else
      ($ ui/stack {:gap "xl"}
        ;; Header — title left; the date-range selector sits here in the page
        ;; chrome (next to refresh), not in its own row, so it reads as governing
        ;; the whole page. Shown only when an analytics block wants it, so a role
        ;; with no analytics sections never sees a spurious selector. Sticky, so
        ;; on a long page the selector + the active period stay reachable while
        ;; scrolling; a hairline separates it from the content when stuck.
        ($ ui/box {:style {:position "sticky"
                           ;; pin below Mantine's fixed AppShell header (its own
                           ;; var; 0 fallback for a non-AppShell host).
                           :top "var(--app-shell-header-height, 0px)"
                           :zIndex 3
                           ;; Match AppShell.Main's own ground (`--surface-page`,
                           ;; not Mantine's cool gray.0) so the stuck bar blends
                           ;; into the page instead of reading as a white slab;
                           ;; it still needs *a* fill to cover content on scroll.
                           :background "var(--surface-page)"
                           :paddingTop "var(--mantine-spacing-xs)"
                           :paddingBottom "var(--mantine-spacing-sm)"}}
          ($ ui/group {:justify "space-between" :align "flex-start" :wrap "wrap"}
            ($ ui/stack {:gap 2}
              ($ ui/title {:order 2} title)
              (when subtitle
                ($ ui/text {:size "sm" :c "dimmed"}
                  ;; The range sub is unset until the user changes the selector,
                  ;; but the selector still *shows* its default — so fall back to
                  ;; that preset's dates, keeping the label and the selector in step.
                  (let [preset (or (:preset date-range) "this-month")
                        dflt   (dates/preset-range preset)
                        start  (or (:start-date date-range) (:start-date dflt))
                        end    (or (:end-date date-range) (:end-date dflt))
                        rl     (dates/human-range start end)]
                    (if rl (str subtitle " — " rl) subtitle)))))
            ($ ui/group {:gap "sm" :align "center" :wrap "nowrap"}
              (when (some :date-selector? blocks)
                ($ date-range/date-range-selector))
              ($ ui/action-icon {:variant "default" :size "lg" :onClick on-refresh}
                ($ icons/icon-refresh {:size 18 :stroke 1.5})))))

        ;; Section groups, top-to-bottom: :live → :snapshot → :analytics.
        (for [{:keys [group] :as block} blocks]
          ($ ui/stack {:key (name group) :gap "xl"}
            (for [s (:sections block)]
              ($ :div {:key (:id s)}
                ($ section/section s)))))))))
